import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { superAdminAPI } from '../../services/api';
import './SuperAdminDashboard.scss';

const SuperAdminDashboard = () => {
  const { superAdmin } = useSuperAdmin();

  const { data: overviewData, isLoading } = useQuery({
    queryKey: ['superadmin-overview'],
    queryFn: superAdminAPI.getOverview
  });

  const overview = overviewData?.data?.data || {};

  const stats = [
    {
      label: 'Total Organizations',
      value: overview?.organizations?.total || 0,
      change: overview?.organizations?.active || 0,
      changeLabel: 'active',
      icon: '🏢',
      color: 'primary'
    },
    {
      label: 'Trial Organizations',
      value: overview?.organizations?.trial || 0,
      change: overview?.organizations?.suspended || 0,
      changeLabel: 'suspended',
      icon: '⏳',
      color: 'warning'
    },
    {
      label: 'Total Users',
      value: overview?.users?.total || 0,
      change: overview?.users?.active || 0,
      changeLabel: 'active',
      icon: '👥',
      color: 'success'
    },
    {
      label: 'Plans Active',
      value: overview?.organizations?.byPlan?.length || 3,
      change: 0,
      changeLabel: '',
      icon: '📋',
      color: 'info'
    }
  ];

  if (isLoading) {
    return (
      <div className="superadmin-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="superadmin-dashboard">
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Platform Overview</h1>
          <p>Welcome back, {superAdmin?.name || 'Super Admin'}</p>
        </div>
        <div className="header-right">
          <Link to="/superadmin/organizations" className="btn btn-primary">
            View Organizations
          </Link>
          <Link to="/superadmin/plans" className="btn btn-outline">
            Manage Plans
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`}>
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <div className="stat-value">{stat.value.toLocaleString()}</div>
              <div className="stat-label">{stat.label}</div>
              {stat.change > 0 && (
                <div className="stat-change">
                  {stat.change.toLocaleString()} {stat.changeLabel}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Organizations by Plan */}
      {overview?.organizations?.byPlan && overview.organizations.byPlan.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2>Organizations by Plan</h2>
          </div>
          <div className="card-body">
            <div className="plan-distribution">
              {overview.organizations.byPlan.map((item, index) => (
                <div key={index} className="plan-item">
                  <div className="plan-name">{item.planName || 'Unknown'}</div>
                  <div className="plan-count">{item.count}</div>
                  <div className="plan-bar">
                    <div
                      className="plan-bar-fill"
                      style={{
                        width: `${(item.count / overview.organizations.total) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/superadmin/organizations" className="action-card">
            <div className="action-icon">🏢</div>
            <div className="action-content">
              <h3>Manage Organizations</h3>
              <p>View, create, and manage all organizations</p>
            </div>
          </Link>
          <Link to="/superadmin/plans" className="action-card">
            <div className="action-icon">📋</div>
            <div className="action-content">
              <h3>Manage Plans</h3>
              <p>Create and edit subscription plans</p>
            </div>
          </Link>
          <Link to="/superadmin/admins" className="action-card">
            <div className="action-icon">👥</div>
            <div className="action-content">
              <h3>Manage Admins</h3>
              <p>Add and manage platform administrators</p>
            </div>
          </Link>
          <Link to="/superadmin/analytics" className="action-card">
            <div className="action-icon">📊</div>
            <div className="action-content">
              <h3>View Analytics</h3>
              <p>Platform usage and revenue metrics</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;